import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { eachDayOfInterval, format, isValid } from 'date-fns';
import { http } from '../../helpers/http';
import { SERVICE_RATE_CALENDAR } from '../../services/Availability';

/**
 * Hook do calendário de preços/disponibilidade (price calendar).
 *
 * Consome o endpoint /rateCalendar e retorna, para o intervalo visível, um
 * mapa de dias: { 'YYYY-MM-DD': { minRate, available } } + a moeda.
 *
 * - Lazy: só busca quando o calendário está aberto (enabled).
 * - Cache por dia (ref): navega entre meses buscando só o que falta; se o
 *   intervalo já está em cache, não chama a API.
 * - Invalida o cache quando muda propriedade/idioma/ocupação.
 */

const DEFAULT_CURRENCY = 'BRL';

const fetchRateCalendar = async ({
  fromDate,
  toDate,
  h,
  s,
  language,
  adults,
  totalChildren,
  children,
  coupon,
}) => {
  const [ok, result] = await http.request(SERVICE_RATE_CALENDAR, {
    l: language,
    h,
    s,
    from: format(fromDate, 'yyyy-MM-dd'),
    to: format(toDate, 'yyyy-MM-dd'),
    adults,
    totalChildren,
    children,
    coupon: coupon || '',
  });
  return ok && result ? result : { currency: null, days: {} };
};

const useRateCalendar = ({
  fromDate,
  toDate,
  adults,
  totalChildren,
  children,
  enabled = true,
}) => {
  const uid = useSelector(state => state.application.property.uid);
  const subpropertyUid = useSelector(
    state => state.application.property.subpropertyUid
  );
  const language = useSelector(state => state.application.i18n.currentLanguage);
  const couponCode = useSelector(state => state.reservation.couponCode);

  const [days, setDays] = useState({});
  const [resolvedCurrency, setResolvedCurrency] = useState(DEFAULT_CURRENCY);
  const [loading, setLoading] = useState(false);

  // Cache autoritativo (ref): { version, days }. A versão muda quando
  // propriedade/idioma/ocupação mudam — aí o cache é invalidado.
  const cacheRef = useRef({ version: null, days: {} });

  const fromKey = isValid(fromDate) ? format(fromDate, 'yyyy-MM-dd') : null;
  const toKey = isValid(toDate) ? format(toDate, 'yyyy-MM-dd') : null;
  const childrenKey = Array.isArray(children) ? children.join(',') : '';

  useEffect(() => {
    if (!enabled || !fromKey || !toKey || !uid) return undefined;

    const version = `${uid}|${subpropertyUid}|${language}|${adults}|${totalChildren}|${childrenKey}|${couponCode || ''}`;
    if (cacheRef.current.version !== version) {
      cacheRef.current = { version, days: {} };
      setDays({});
    }

    const cache = cacheRef.current.days;
    // dias do intervalo visível que ainda não estão no cache
    const missing = eachDayOfInterval({ start: fromDate, end: toDate }).filter(
      d => !cache[format(d, 'yyyy-MM-dd')]
    );

    // tudo já em cache → nenhuma chamada à API
    if (missing.length === 0) return undefined;

    // busca só a faixa que falta (mês novo), não o intervalo visível inteiro
    const fetchFrom = missing[0];
    const fetchTo = missing[missing.length - 1];

    let cancelled = false;
    setLoading(true);

    fetchRateCalendar({
      fromDate: fetchFrom,
      toDate: fetchTo,
      h: uid,
      s: subpropertyUid,
      language,
      adults,
      totalChildren,
      children,
      coupon: couponCode,
    })
      .then(result => {
        if (cancelled) return;
        cacheRef.current.days = {
          ...cacheRef.current.days,
          ...(result.days || {}),
        };
        setDays({ ...cacheRef.current.days });
        if (result.currency) setResolvedCurrency(result.currency);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fromKey,
    toKey,
    uid,
    subpropertyUid,
    language,
    adults,
    totalChildren,
    childrenKey,
    couponCode,
    enabled,
  ]);

  return { days, currency: resolvedCurrency, loading };
};

export default useRateCalendar;
